import { useState, useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';

interface MicrophoneStreamOptions {
  isMicActive: boolean;
}

export const useMicrophoneStream = ({ isMicActive }: MicrophoneStreamOptions) => {
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [micReady, setMicReady] = useState(false);
  const { toast } = useToast();
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    let currentStream: MediaStream | null = null;
    
    const setupMicrophone = async () => {
      if (!isMicActive) {
        // Stop current stream
        if (micStream) {
          micStream.getTracks().forEach(track => {
            console.log('[Microphone] Stopping track:', track.kind);
            track.stop();
          });
          setMicStream(null);
        }
        
        // Stop audio analysis
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
          analyserRef.current = null;
        }
        
        setMicReady(false);
        setAudioLevel(0);
        return;
      }

      try {
        console.log('[Microphone] Requesting microphone access...');
        
        currentStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000
          }
        });

        console.log('[Microphone] Stream obtained:', currentStream.id);
        setMicStream(currentStream);
        setMicReady(true);

        // Set up audio level monitoring
        audioContextRef.current = new AudioContext();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        
        const source = audioContextRef.current.createMediaStreamSource(currentStream);
        source.connect(analyserRef.current);

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        
        const updateAudioLevel = () => {
          if (analyserRef.current) {
            analyserRef.current.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
            const normalized = Math.min(1, average / 128);
            setAudioLevel(normalized);
          }
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        };
        
        updateAudioLevel();

      } catch (err) {
        console.error('[Microphone] Error accessing microphone:', err);
        setMicReady(false);
        
        toast({
          title: "Microphone access error",
          description: "Could not access your microphone. Please check your browser permissions.",
          variant: "destructive"
        });
      }
    };

    setupMicrophone();

    return () => {
      console.log('[Microphone] Cleanup...');
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [isMicActive, toast]);

  return {
    micStream,
    micReady,
    audioLevel
  };
};
